import React, { useEffect, useState } from "react";
import { LazyLoadedGoogleModelViewer } from "@wss/model-viewer";
import { useSwipeable } from "react-swipeable";
import { IBowser } from "bowser";
import { ReactZoomPanPinchRef, TransformWrapper } from "react-zoom-pan-pinch";
import { Modal, ModalBody, ModalHeader, ModalTrigger } from "@clarkinc/zest-design-system/ui";
import { Image, ModelFiles, Video } from "../model";
import ImageMediaRenderer from "~/common/MediaViewerV2/ModalDisplay/ImageRenderers/ImageMediaRenderer";
import ImageGalleryRenderer from "~/common/MediaViewerV2/ModalDisplay/ImageRenderers/ImageGalleryRenderer";
import ModelMediaRenderer from "~/common/MediaViewerV2/ModalDisplay/ModelRenderers/ModelMediaRenderer";
import ModelGalleryRenderer from "~/common/MediaViewerV2/ModalDisplay/ModelRenderers/ModelGalleryRenderer";
import VideoMediaRenderer from "~/common/MediaViewerV2/ModalDisplay/VideoRenderers/VideoMediaRenderer";
import VideoGalleryRenderer from "~/common/MediaViewerV2/ModalDisplay/VideoRenderers/VideoGalleryRenderer";
import { isWebGlSupported } from "../utils";
export interface ModalDisplayProps {
  openZoom: () => void;
  overlayOpen: boolean;
  setOverlayOpen: (b: boolean) => void;
  isMobileScreenSize: boolean;
  media: (Image | Video | ModelFiles)[];
  selectedMediaIndex: number;
  setSelectedMediaIndex: React.Dispatch<React.SetStateAction<number>>;
  title: string;
  placeholderImageUrl: string;
  bowser?: Readonly<IBowser>;
  slides: React.MutableRefObject<{
    [p: string]: HTMLDivElement;
  }>;
  productDimensionsIsEnabled?: boolean;
}
const ModalDisplay = ({
  openZoom,
  overlayOpen,
  setOverlayOpen,
  isMobileScreenSize,
  media,
  selectedMediaIndex,
  setSelectedMediaIndex,
  title,
  placeholderImageUrl,
  bowser,
  slides,
  productDimensionsIsEnabled
}: ModalDisplayProps) => {
  const [hasFragmentLoaded, setHasFragmentLoaded] = React.useState<boolean>(false);
  const [mobilePanDisabled, setMobilePanDisabled] = useState(true);
  const galleryContainerStyles = isMobileScreenSize ? "flex list-none m-0 mb-4 absolute bottom-0 left-1/2 transform -translate-x-1/2" : "grid list-none mt-2 mr-2 p-1 max-h-full overflow-y-auto absolute top-0 right-0 gap-2 grid-cols-2";
  const swipeHandlers = useSwipeable({
    onSwipedLeft: eventData => {
      if (mobilePanDisabled && isMobileScreenSize) {
        handleOverlayImgChange(eventData.event, "right");
      }
    },
    onSwipedRight: eventData => {
      if (mobilePanDisabled && isMobileScreenSize) {
        handleOverlayImgChange(eventData.event, "left");
      }
    }
  });
  const onClose = () => {
    const focusedSlideModelViewer = slides.current[selectedMediaIndex]?.querySelector("model-viewer");
    const focusedSlideButton = slides.current[selectedMediaIndex]?.querySelector("button");
    if (focusedSlideModelViewer) {
      const modelViewer = focusedSlideModelViewer?.shadowRoot?.querySelector('div[tabindex="0"]');
      (modelViewer as HTMLElement)?.focus();
    } else {
      (focusedSlideButton as HTMLElement).focus();
    }
    setOverlayOpen(false);
  };
  const closeOverlay = (e: React.MouseEvent) => {
    e.preventDefault();
    setOverlayOpen(false);
    let body;
    if (typeof window !== "undefined" && window.document) {
      body = document.body;
    }
    body?.classList.remove("overflow-hidden");
    slides.current[selectedMediaIndex]?.scrollIntoView({
      block: "nearest"
    });
  };
  const handleOverlayZoom = (zoomRef: ReactZoomPanPinchRef) => {
    const panningDisabled = zoomRef.state.scale <= 1;
    if (panningDisabled !== mobilePanDisabled) {
      setMobilePanDisabled(panningDisabled);
    }
  };
  const handleOverlayImgChange = (e: React.MouseEvent | TouchEvent | MouseEvent, align?: "left" | "right") => {
    if (!align) {
      e.preventDefault();
      const {
        currentTarget
      } = e;
      if (!(currentTarget instanceof HTMLElement)) return;

      // The href is used as a url fragment in the following structure:
      // '#modal-{fragmentMediaType}-{selectedMediaIndex}'
      const newIndex = parseInt(currentTarget?.getAttribute("href")?.split("-")[2] || "1", 10);
      setSelectedMediaIndex(newIndex);
      return;
    }
    if (e instanceof TouchEvent) {
      if (e.changedTouches.length > 1) return;
    }
    if (align === "left") {
      setSelectedMediaIndex(selected => {
        if (selected - 1 < 0) {
          return media.length - 1;
        }
        return selected - 1;
      });
    } else {
      setSelectedMediaIndex(selected => {
        if (selected + 1 >= media.length) {
          return 0;
        }
        return selected + 1;
      });
    }
  };
  const mediaRenderer = () => {
    const currentOverlayMedia = media[selectedMediaIndex];
    if (!currentOverlayMedia) return null;
    if ("isPrimaryDetailImage" in currentOverlayMedia) {
      return <ImageMediaRenderer isMobileScreenSize={isMobileScreenSize} image={currentOverlayMedia} title={title} overlayOpen={overlayOpen} openZoom={openZoom} bowser={bowser} placeholderImageUrl={placeholderImageUrl} />;
    }
    if ("relativeGlbFilePath" in currentOverlayMedia) {
      return <ModelMediaRenderer model={currentOverlayMedia} productDimensionsIsEnabled={productDimensionsIsEnabled} />;
    }
    return <VideoMediaRenderer video={currentOverlayMedia} selectedMediaIndex={selectedMediaIndex} data-sentry-element="VideoMediaRenderer" data-sentry-component="mediaRenderer" data-sentry-source-file="ModalDisplay.tsx" />;
  };
  const galleryRenderer = (medium: Image | Video | ModelFiles, index: number) => {
    if ("isPrimaryDetailImage" in medium) {
      return <ImageGalleryRenderer key={`media${index}`} image={medium} index={index} handleOverlayImageChange={handleOverlayImgChange} />;
    }
    if ("relativeGlbFilePath" in medium) {
      return isWebGlSupported && <ModelGalleryRenderer key={`media${index}`} index={index} handleOverlayImageChange={handleOverlayImgChange} productDimensionsIsEnabled={productDimensionsIsEnabled ?? false} />;
    }
    return <VideoGalleryRenderer key={`media${index}`} index={index} video={medium} handleOverlayImageChange={handleOverlayImgChange} data-sentry-element="VideoGalleryRenderer" data-sentry-component="galleryRenderer" data-sentry-source-file="ModalDisplay.tsx" />;
  };
  const mobileGalleryRenderer = (medium: Image | Video | ModelFiles, index: number) => {
    if ("relativeGlbFilePath" in medium) return null;
    return <li className="m-1" key={`pic-${index}`} data-sentry-component="mobileGalleryRenderer" data-sentry-source-file="ModalDisplay.tsx">
        <div className={`${index === selectedMediaIndex ? "opacity-50" : "opacity-20"} bg-black rounded-full h-2 w-2`} />
      </li>;
  };
  const stopPropagation = (event: {
    stopPropagation: () => void;
  }) => {
    event.stopPropagation();
  };

  // Handles opening URL fragment on page load within the modal
  useEffect(() => {
    const hash = window.location.hash.split("-");
    const isModalFragment = hash[0] === "#modal";
    const fragmentMediaType = hash[1];
    const fragmentMediaIndex = hash[2];
    if (!isModalFragment || !fragmentMediaIndex) return;

    // Because Model Viewer is Lazy Loaded, check to see if it should be loaded now
    if (fragmentMediaType === "model") LazyLoadedGoogleModelViewer.load();

    // Open the modal and select the item based on what was in the fragment
    setOverlayOpen(true);
    // Add a small delay to allow the DOM to update
    setTimeout(() => setSelectedMediaIndex(parseInt(fragmentMediaIndex, 10)), 100);
    setHasFragmentLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Modal open={overlayOpen} onOpenChange={isModalOpen => {
    const hash = window.location.hash.split("-");
    const isModalFragment = hash[0] === "#modal";
    if (!isModalOpen && (
    // We dont want this to run until the fragment has a chance to load
    isModalFragment && hasFragmentLoaded || !isModalFragment)) {
      onClose();
    }
  }} aria-label="Image Gallery" id="imageGalleryModal" theme="headless" style={{
    animation: "none",
    transform: "none"
  }} className="h-screen w-screen border-none top-1/2 left-1/2 fixed z-1000" data-sentry-element="Modal" data-sentry-component="ModalDisplay" data-sentry-source-file="ModalDisplay.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="ModalDisplay.tsx">Image Gallery</ModalHeader>
      {/* Necessary to stop propagation for these events or else the Zest Modal event
        listeners will interfere with the react-zoom-pan-pinch package listeners */}
      <ModalBody onDoubleClick={stopPropagation} onWheel={stopPropagation} onTouchStart={stopPropagation} onTouchMove={stopPropagation} onTouchEnd={stopPropagation} data-sentry-element="ModalBody" data-sentry-source-file="ModalDisplay.tsx">
        <div className="bg-white flex flex-wrap items-center h-screen overflow-hidden fixed top-0 left-0 w-full z-50" data-testid="imageModal">
          <TransformWrapper initialScale={1} disabled={!isMobileScreenSize} onZoom={handleOverlayZoom} panning={{
          disabled: mobilePanDisabled
        }} data-sentry-element="TransformWrapper" data-sentry-source-file="ModalDisplay.tsx">
            <>
              <ModalTrigger type="button" className="
                  appearance-none
                  rounded
                  border-none
                  cursor-pointer
                  flex
                  items-center
                  justify-center
                  h-10
                  text-3xl
                  leading-4
                  m-4
                  p-0
                  absolute
                  top-0
                  right-0
                  text-gray-800
                  w-10
                  z-10
                  md:left-0
                  md:right-auto
                " style={{
              background: "rgba(255, 255, 255, 0.4)"
            }} onClick={closeOverlay} data-sentry-element="ModalTrigger" data-sentry-source-file="ModalDisplay.tsx">
                <span aria-hidden>&times;</span>
                <span className="sr-only">close image gallery dialog</span>
              </ModalTrigger>

              <div className="box-border flex justify-center h-full p-2 relative text-center w-full" {...swipeHandlers}>
                <div className={`
                    grid
                    overflow-hidden
                    relative w-full
                    ${!isMobileScreenSize && "pr-[180px]"}
                  `}>
                  <svg viewBox="0 0 1 1" style={{
                  gridArea: "1 / 1",
                  maxWidth: "100%"
                }} data-sentry-element="svg" data-sentry-source-file="ModalDisplay.tsx" />
                  <div style={{
                  gridArea: "1 / 1"
                }}>{mediaRenderer()}</div>
                </div>
              </div>

              <ul className={galleryContainerStyles}>
                {isMobileScreenSize ? media.map((medium, index) => mobileGalleryRenderer(medium, index)) : media.map((medium, index) => galleryRenderer(medium, index))}
              </ul>
            </>
          </TransformWrapper>
        </div>
      </ModalBody>
    </Modal>;
};
export default ModalDisplay;